import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CurrentStateService } from '@ddv/behaviors';
import { DropdownOption, ModalDialogActive, MultiSubscriptionComponent } from '@ddv/common-components';
import { ManagerService } from '@ddv/layout';
import {
    MODE,
    VIZ_STYLE_CLASS,
    QueryTypeName,
    UserPreferences,
    AppWidgetState,
    Widget,
    DisplayNameType,
    VisualizationType,
    TableSortType,
} from '@ddv/models';

import { WidgetSettingsOverrides } from '../../../models/widget-settings-overrides';

// this is dynamically launched as a modal, so you won't see any references to the selector
// it is only launched by the WidgetHeaderComponent
@Component({
    selector: 'app-widget-settings-overrides',
    templateUrl: './widget-settings-overrides.component.html',
    styleUrls: ['./widget-settings-overrides.component.scss'],
    standalone: false,
})
export class WidgetSettingsOverridesComponent extends MultiSubscriptionComponent implements OnInit {
    @Input() widgetState: AppWidgetState | undefined;
    @Input() overrides: WidgetSettingsOverrides | undefined;
    @Input() userPreferences: UserPreferences | undefined;
    @Input() allowJumpToManageWidgets = false;
    @Input() inFilterOnlyMode = false;
    @Input() canManageCrosstalk = false;
    @Input() crosstalkManagementPageURL = '';

    @Output() saveClicked = new EventEmitter<WidgetSettingsOverrides>();
    @Output() manageWidgetClicked = new EventEmitter();

    effectiveState: WidgetSettingsOverrides | undefined;

    visualizationOptions: DropdownOption[] | undefined;
    initialVisualization: DropdownOption | undefined;

    slicerOptions: DropdownOption[] | undefined;
    initialSlicer: DropdownOption | undefined;

    sortOptions: DropdownOption[] | undefined;
    sortOn: DropdownOption | undefined;

    sortDirectionOptions: DropdownOption[] | undefined;
    sortDirection: DropdownOption | undefined;

    isMultiClient = false;
    isViewMode = false;
    widgetConfig: Widget | undefined;
    hasRealtimeUpdateOption = false;
    visualizationIconClass: string | undefined;
    isCrosstalkGrid = false;

    constructor(
        protected modalDialogActive: ModalDialogActive<void>,
        private readonly currentStateService: CurrentStateService,
        private readonly managerService: ManagerService,
    ) {
        super();
    }

    ngOnInit(): void {
        if (!this.overrides?.subscribedToDashboardFilters) {
            this.setSubscriptionState();
        }

        this.subscribeTo(this.currentStateService.isMultiClient$, (isMultiClient) => {
            this.isMultiClient = isMultiClient;
        });

        this.subscribeTo(this.currentStateService.dashboardModeAndId$, ({ mode }) => {
            this.isViewMode = mode === MODE.VIEW;
        });

        this.widgetConfig = this.managerService.getWidgetById(this.widgetState?.id ?? 0);

        this.recomputeState();

        this.setVisualizationIcon();

        this.setHasRealtimeUpdate();

        this.setIsCrosstalkGrid();
    }

    protected applyDisplayNameType(value: DisplayNameType): void {
        this.overrides?.applyDisplayNameType(value);
        this.recomputeState();
    }

    protected applyCustomName(value: string): void {
        this.overrides?.applyCustomName(value);
        this.recomputeState();
    }

    protected applyIsSubscribedToDashboardFilters(value: boolean): void {
        this.overrides?.applyIsSubscribedToDashboardFilters(value);

        this.recomputeState();
    }

    protected applyMasterWidget(value: boolean): void {
        this.overrides?.applyMasterWidget(value);
        this.recomputeState();
    }

    protected applyRealTimeUpdates(value: boolean): void {
        this.overrides?.applyRealtimeUpdates(value);

        this.recomputeState();
    }

    protected applyDefaultVisualizationType(value: VisualizationType | null): void {
        this.overrides?.applyDefaultVisualizationType(value);

        this.recomputeState();

        this.setVisualizationIcon();
    }

    protected applyDefaultSlicerForDefaultVisualization(value: string | null): void {
        this.overrides?.applyDefaultSlicerForDefaultVisualization(value);
        this.recomputeState();
    }

    protected applyDefaultSortOnForDefaultVisualization(value: TableSortType | null): void {
        this.overrides?.applyDefaultSortOnForDefaultVisualization(value);
        this.recomputeState();
    }

    protected applyDefaultSortDirectionForDefaultVisualization(value: 'ASC' | 'DESC' | 'CUSTOM' | null): void {
        this.overrides?.applyDefaultSortDirectionForDefaultVisualization(value);
        this.recomputeState();
    }

    protected applyOverrides(): void {
        this.saveClicked.emit(this.overrides);
    }

    private setIsCrosstalkGrid(): void {
        this.isCrosstalkGrid = this.isAdvancedGrid() &&
            !!this.widgetConfig?.extraParameters?.preferences?.namedQuery?.normalizeConversableType();
    }

    private setSubscriptionState(): void {
        this.overrides?.applyIsSubscribedToDashboardFilters(!!this.widgetState?.isSubscribedToDashboardFilters);
    }

    private recomputeState(): void {
        if (!this.widgetState) {
            return;
        }

        const state = this.overrides?.deriveEffectiveStateFrom(this.widgetState);
        if (!state) {
            return;
        }

        this.effectiveState = state;

        if (this.isMultiClient) {
            state.subscribedToDashboardFilters = true;
        }

        this.visualizationOptions = this.widgetState.configuredVisualizationTypes.map((t) => ({ text: t, key: t, value: t }));
        const defaultVisType = state.defaultVisualizationType ?? '';
        this.initialVisualization = { text: defaultVisType, key: defaultVisType, value: defaultVisType };

        this.slicerOptions = this.widgetState.getSlicersForVisualizationType(state.defaultVisualizationType!)
            .map((t) => ({ text: t ?? '', key: t, value: t }));
        const defaultSlicer = state.defaultSlicerForDefaultVisualization;
        this.initialSlicer = defaultSlicer ?
            { text: defaultSlicer, key: defaultSlicer, value: defaultSlicer } :
            this.slicerOptions[0];

        this.sortOptions = this.widgetState.getSortOnChoicesForVisualizationType(state.defaultVisualizationType!)
            .map((t) => ({ text: t.label, key: t.value, value: t.value }));
        this.sortOn = this.sortOptions.find((s) => s.value === state.defaultSortOnForDefaultVisualization);

        this.sortDirectionOptions = this.widgetState
            .getDefaultSortDirectionOptionsForDefaultVisualization(state.defaultSortOnForDefaultVisualization!)
            .map((s) => ({ text: s.value, key: s.label, value: s.value }));
        this.sortDirection = this.sortDirectionOptions
            .find((s) => s.value === state.defaultSortDirectionForDefaultVisualization);
    }

    private setVisualizationIcon(): void {
        this.visualizationIconClass = VIZ_STYLE_CLASS[this.effectiveState!.defaultVisualizationType!];
    }

    private setHasRealtimeUpdate(): void {
        const queryTypeName = this.widgetState?.namedQuery?.normalizeQueryTypeName();
        this.hasRealtimeUpdateOption = queryTypeName === QueryTypeName.EOD ||
            queryTypeName === QueryTypeName.TRADE_FILE_LOADER ||
            queryTypeName === QueryTypeName.TRADE_FILE_SUMMARY;
    }

    private isAdvancedGrid(): boolean {
        return this.widgetConfig?.extraParameters?.preferences?.currentVisualization === 'ADVANCED_GRID';
    }
}
